/**
 * Composes the music loop of a level from its seed and theme (a boss level's from its boss's
 * own style). Pure and deterministic (the shared sfc32 RNG): the same level always gets the same
 * tune, on every device.
 *
 * Form: four 4-bar phrases, A A' B A (16 bars). A' repeats A with a new ending, B brings a
 * second progression, a new melody and accompaniment. Every phrase ends with a drum fill,
 * phrases A and B start with a crash. The melody is motif based: bar 3 of a phrase repeats
 * the rhythm of bar 1 and its shape moved to the new chord, strong beats land on chord
 * tones, the last note of a phrase resolves to the root or third.
 *
 * Tempo is not part of the song: the sequencer plays it at the level's bpm.
 *
 * A holiday (`season`) plays the same level in its holiday style (styles.ts, holidayStyle):
 * the same random choices, so the same rhythms, in a spooky or a festive dress.
 */

import type { BossKind } from '@cube-rush/shared';
import { hash32, Rng } from '@cube-rush/shared';
import type { Season } from '../../season/season';
import {
  type Accompaniment,
  BOSS_STYLES,
  holidayStyle,
  type MusicStyle,
  musicStyle,
} from './styles';
import { inRegister, nearestChordTone, SCALES, type Scale, scaleNote, triad } from './theory';

export const STEPS_PER_BAR = 16;
export const SONG_BARS = 16;
const PHRASE_BARS = 4;

/** chime: a deep church bell (Halloween phrases); sleigh: sleigh bells (winter hi-hats). */
export type DrumKind =
  | 'kick'
  | 'snare'
  | 'clap'
  | 'hat'
  | 'openHat'
  | 'tom'
  | 'crash'
  | 'chime'
  | 'sleigh';
export type SongVoice = 'lead' | 'arp' | 'bass' | DrumKind;

export interface SongEvent {
  /** Sixteenth-note step inside the loop. */
  step: number;
  voice: SongVoice;
  /** MIDI note (toms: their pitch; other drums ignore it). */
  midi: number;
  /** Length in steps. */
  length: number;
  /** 0..1 */
  velocity: number;
}

export interface Song {
  style: MusicStyle;
  bars: number;
  /** Steps in the loop. */
  length: number;
  /** Pitch class of the key (0 = C). */
  tonic: number;
  scale: Scale;
  /** Every note of the loop, sorted by step. */
  events: readonly SongEvent[];
}

/** Rhythms of melody bars (`x` = note). */
const RHYTHMS = [
  'x...x...x.x.x...',
  'x.x.x...x.......',
  'x..x..x.x...x...',
  'x...x.x.x...x.x.',
  'x.......x.x.x...',
  'x.x...x.x.x.....',
  'x...x...x...x...',
  'x..x..x...x.x...',
  'x.x.x.x.x...x...',
] as const;

/** Rhythms of the last bar of a phrase: they settle on a long final note. */
const CADENCES = [
  'x...x...x.......',
  'x.x.x...x.......',
  'x.......x.......',
  'x.x.x.x.x.......',
  'x...x.......x...',
] as const;

/** Melody range in scale indices around the lead's tonic. */
const MELODY_LOW = -2;
const MELODY_HIGH = 9;

/** Registers (lowest MIDI note of the octave each part's tonic is placed in). */
const LEAD_REGISTER = 66;
const ARP_REGISTER = 57;
const BASS_REGISTER = 40;
/** The church bell rings the tonic in this octave. */
const CHIME_REGISTER = 50;

type Progression = readonly [number, number, number, number];

interface MelodyNote {
  /** Step inside the phrase (0..63). */
  step: number;
  length: number;
  /** Scale index relative to the lead's tonic. */
  index: number;
  velocity: number;
}

const SALT = 0x6d757369; // "musi"

export function composeSong(
  seed: number,
  theme: number,
  season: Season = 'none',
  boss?: BossKind,
): Song {
  // A boss level has the boss's own style; any other level its theme's, in the holiday's version.
  const style = boss === undefined ? holidayStyle(musicStyle(theme), season) : BOSS_STYLES[boss];
  const scale = SCALES[style.scale];
  const rng = new Rng(hash32(seed, theme, SALT));
  const tonic = rng.int(0, 11);
  const progA = rng.pick(style.progressions);
  const progB = pickOther(rng, style.progressions, progA);
  const groove = rng.pick(style.grooves);
  const bassPattern = rng.pick(style.bassPatterns);
  const accA = rng.pick(style.accompaniments);
  const accB = pickOther(rng, style.accompaniments, accA);

  const rhythmA = [rng.pick(RHYTHMS), rng.pick(RHYTHMS)] as const;
  const melodyA = phraseMelody(
    rng.fork(1),
    progA,
    [rhythmA[0], rhythmA[1], rhythmA[0], rng.pick(CADENCES)],
    style.leadDensity,
    2,
  );
  // A': the same first half, a new second half that continues from it.
  const firstHalf = melodyA.filter((n) => n.step < 2 * STEPS_PER_BAR);
  const endingA2 = phraseMelody(
    rng.fork(2),
    progA,
    [rhythmA[0], rhythmA[1], rng.pick(RHYTHMS), rng.pick(CADENCES)],
    style.leadDensity,
    lastIndex(firstHalf, 2),
    2,
  );
  const melodyA2 = [...firstHalf, ...endingA2];
  const rhythmB = rng.pick(RHYTHMS);
  const melodyB = phraseMelody(
    rng.fork(3),
    progB,
    [rhythmB, rhythmB, rng.pick(RHYTHMS), rng.pick(CADENCES)],
    style.leadDensity,
    6,
  );

  const sections: ReadonlyArray<{
    prog: Progression;
    melody: readonly MelodyNote[];
    acc: Accompaniment;
  }> = [
    { prog: progA, melody: melodyA, acc: accA },
    { prog: progA, melody: melodyA2, acc: accA },
    { prog: progB, melody: melodyB, acc: accB },
    { prog: progA, melody: melodyA, acc: accA },
  ];

  const leadTonic = inRegister(tonic, LEAD_REGISTER);
  const arpTonic = inRegister(tonic, ARP_REGISTER);
  const bassTonic = inRegister(tonic, BASS_REGISTER);
  const chimeTonic = inRegister(tonic, CHIME_REGISTER);
  const fillRng = rng.fork(4);
  const events: SongEvent[] = [];

  sections.forEach((section, s) => {
    const sectionStart = s * PHRASE_BARS * STEPS_PER_BAR;
    for (const note of section.melody) {
      events.push({
        step: sectionStart + note.step,
        voice: 'lead',
        midi: scaleNote(leadTonic, scale, note.index),
        length: note.length,
        velocity: note.velocity,
      });
    }
    for (let bar = 0; bar < PHRASE_BARS; bar++) {
      const barStart = sectionStart + bar * STEPS_PER_BAR;
      const degree = section.prog[bar] ?? 0;
      const nextDegree = section.prog[bar + 1] ?? sections[(s + 1) % sections.length]?.prog[0] ?? 0;
      accompanimentBar(events, section.acc, barStart, degree, arpTonic, scale);
      bassBar(events, bassPattern, barStart, degree, nextDegree, bassTonic, scale);
      const fill =
        bar === PHRASE_BARS - 1 ? (s === sections.length - 1 ? 'long' : 'short') : 'none';
      drumBar(events, style, groove, barStart, fill, fillRng);
      if (bar === 0 && s !== 1) {
        events.push(
          style.bells
            ? { step: barStart, voice: 'chime', midi: chimeTonic, length: 16, velocity: 0.8 }
            : { step: barStart, voice: 'crash', midi: 0, length: 16, velocity: 0.8 },
        );
      }
    }
  });

  events.sort((a, b) => a.step - b.step);
  const bars = sections.length * PHRASE_BARS;
  return { style, bars, length: bars * STEPS_PER_BAR, tonic, scale, events };
}

/** A different item than `current` when the list has one. */
function pickOther<T>(rng: Rng, items: readonly T[], current: T): T {
  for (let attempt = 0; attempt < 4; attempt++) {
    const item = rng.pick(items);
    if (item !== current) return item;
  }
  return current;
}

function lastIndex(notes: readonly MelodyNote[], fallback: number): number {
  // Not notes.at(-1): Array.prototype.at is missing before iOS 15.4 (no music at all there).
  return notes[notes.length - 1]?.index ?? fallback;
}

/** Onsets of a rhythm string with the length of each note (until the next onset). */
function onsets(rhythm: string, cadence: boolean): Array<{ step: number; length: number }> {
  const steps: number[] = [];
  for (let i = 0; i < rhythm.length; i++) if (rhythm[i] === 'x') steps.push(i);
  return steps.map((step, i) => {
    const next = steps[i + 1] ?? STEPS_PER_BAR;
    const last = i === steps.length - 1;
    return { step, length: Math.min(next - step, cadence && last ? 8 : 4) };
  });
}

/**
 * A phrase of melody over four chords, from bar `fromBar` on. `start` = the scale index
 * the tune continues from.
 */
function phraseMelody(
  rng: Rng,
  prog: Progression,
  rhythms: readonly [string, string, string, string],
  density: number,
  start: number,
  fromBar = 0,
): MelodyNote[] {
  const notes: MelodyNote[] = [];
  let previous = start;
  let direction = rng.chance(0.5) ? 1 : -1;
  let firstBar: MelodyNote[] = [];

  for (let bar = fromBar; bar < PHRASE_BARS; bar++) {
    const degree = prog[bar] ?? 0;
    const barStart = bar * STEPS_PER_BAR;
    const cadence = bar === PHRASE_BARS - 1;

    // Bar 3 with bar 1's rhythm: a sequence (bar 1 moved along with the chord change).
    if (bar === 2 && rhythms[2] === rhythms[0] && firstBar.length > 0) {
      // Move by the chord change, the short way round (never more than a fourth).
      let shift = (((degree - (prog[0] ?? 0)) % 7) + 7) % 7;
      if (shift > 3) shift -= 7;
      for (const note of firstBar) {
        let index = clamp(note.index + shift, MELODY_LOW, MELODY_HIGH);
        if (note.step % 8 === 0) index = nearestChordTone(index, degree);
        notes.push({ ...note, step: barStart + note.step, index });
        previous = index;
      }
      continue;
    }

    const barNotes: MelodyNote[] = [];
    const notesOfBar = onsets(rhythms[bar] ?? RHYTHMS[0], cadence);
    notesOfBar.forEach((onset, i) => {
      const strong = onset.step % 8 === 0;
      const last = i === notesOfBar.length - 1;
      if (!strong && !last && i > 0 && !rng.chance(density)) return;
      let index: number;
      if (cadence && last) {
        // Resolve: the root or the third of the last chord, whichever is closer.
        const root = nearestOctave(degree, previous);
        const third = nearestOctave(degree + 2, previous);
        index = Math.abs(root - previous) <= Math.abs(third - previous) ? root : third;
      } else if (i === 0 || strong) {
        index = nearestChordTone(previous + direction, degree);
      } else {
        const move = rng.weighted<number>([
          [1, 5],
          [2, 1.5],
          [0, 1],
        ]);
        index = previous + move * direction;
        if (rng.chance(0.3)) direction = -direction;
      }
      if (index > MELODY_HIGH) {
        index = nearestChordTone(MELODY_HIGH - 1, degree);
        direction = -1;
      } else if (index < MELODY_LOW) {
        index = nearestChordTone(MELODY_LOW + 1, degree);
        direction = 1;
      }
      barNotes.push({
        step: onset.step,
        length: onset.length,
        index,
        velocity: strong ? 0.95 : 0.8,
      });
      previous = index;
    });
    if (bar === 0) firstBar = barNotes;
    for (const note of barNotes) notes.push({ ...note, step: barStart + note.step });
  }
  return notes;
}

/** The octave of scale index `index` closest to `near`. */
function nearestOctave(index: number, near: number): number {
  return index + 7 * Math.round((near - index) / 7);
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** Chords sit around the arp's tonic: high degrees move an octave down. */
function chordBase(degree: number): number {
  return degree >= 4 ? degree - 7 : degree;
}

function accompanimentBar(
  events: SongEvent[],
  acc: Accompaniment,
  barStart: number,
  degree: number,
  arpTonic: number,
  scale: Scale,
): void {
  const chord = triad(chordBase(degree));
  if (acc === 'stabs') {
    for (const offbeat of [2, 6, 10, 14]) {
      for (const index of chord) {
        events.push({
          step: barStart + offbeat,
          voice: 'arp',
          midi: scaleNote(arpTonic, scale, index),
          length: 1,
          velocity: 0.6,
        });
      }
    }
    return;
  }
  for (let step = 0, n = 0; step < STEPS_PER_BAR; step += acc.rate, n++) {
    const tone = acc.order[n % acc.order.length] ?? 0;
    const index = tone === 3 ? chord[0] + 7 : (chord[tone] ?? chord[0]);
    events.push({
      step: barStart + step,
      voice: 'arp',
      midi: scaleNote(arpTonic, scale, index),
      length: acc.rate,
      velocity: step % 4 === 0 ? 0.75 : 0.55,
    });
  }
}

function bassBar(
  events: SongEvent[],
  pattern: string,
  barStart: number,
  degree: number,
  nextDegree: number,
  bassTonic: number,
  scale: Scale,
): void {
  const root = degree >= 5 ? degree - 7 : degree;
  const nextRoot = nextDegree >= 5 ? nextDegree - 7 : nextDegree;
  const notes: Array<{ step: number; midi: number }> = [];
  for (let step = 0; step < STEPS_PER_BAR; step++) {
    const symbol = pattern[step];
    let index: number | null = null;
    if (symbol === 'r' || symbol === 'o') index = root;
    else if (symbol === 'f') index = root + 4;
    else if (symbol === 'a') index = nextRoot - 1;
    if (index === null) continue;
    notes.push({ step, midi: scaleNote(bassTonic, scale, index) + (symbol === 'o' ? 12 : 0) });
  }
  notes.forEach((note, i) => {
    const next = notes[i + 1]?.step ?? STEPS_PER_BAR;
    events.push({
      step: barStart + note.step,
      voice: 'bass',
      midi: note.midi,
      length: next - note.step,
      velocity: note.step % 4 === 0 ? 0.9 : 0.75,
    });
  });
}

/** Tom pitches of the fills, high to low (MIDI). */
const TOMS = [57, 52, 48, 45] as const;

function drumBar(
  events: SongEvent[],
  style: MusicStyle,
  groove: { kick: string; snare: string; hat: string; open?: string },
  barStart: number,
  fill: 'none' | 'short' | 'long',
  rng: Rng,
): void {
  const fillFrom = fill === 'long' ? 8 : fill === 'short' ? 12 : STEPS_PER_BAR;
  const snare = style.kit === 'electro' ? 'clap' : 'snare';
  for (let step = 0; step < fillFrom; step++) {
    const at = barStart + step;
    const beat = step % 4 === 0;
    if (groove.kick[step] === 'x') events.push(drum(at, 'kick', beat ? 1 : 0.8));
    if (groove.snare[step] === 'x') events.push(drum(at, snare, beat ? 0.95 : 0.7));
    if (groove.hat[step] === 'x') {
      // Sleigh bells shake on the eighth notes only (sixteenths would be a rattle).
      if (!style.jingle) events.push(drum(at, 'hat', beat ? 0.9 : step % 2 === 0 ? 0.65 : 0.45));
      else if (step % 2 === 0) events.push(drum(at, 'sleigh', beat ? 0.9 : 0.65));
    }
    if (groove.open?.[step] === 'x') events.push(drum(at, 'openHat', 0.7));
  }
  if (fill === 'none') return;
  if (fill === 'long' || rng.chance(0.5)) {
    // Toms rolling down, then a snare to land the phrase.
    const count = STEPS_PER_BAR - fillFrom;
    for (let i = 0; i < count; i++) {
      const step = fillFrom + i;
      const last = i === count - 1;
      if (fill === 'long' && i % 2 === 1 && !last) continue;
      const tom = TOMS[Math.min(TOMS.length - 1, Math.floor((i * TOMS.length) / count))] ?? 48;
      events.push(
        last
          ? drum(barStart + step, snare, 1)
          : { ...drum(barStart + step, 'tom', 0.85), midi: tom },
      );
    }
    events.push(drum(barStart + fillFrom, 'kick', 0.9));
    return;
  }
  // A short snare roll.
  for (const step of [12, 14, 15])
    events.push(drum(barStart + step, snare, step === 12 ? 0.9 : 0.75));
  events.push(drum(barStart + 12, 'kick', 0.9));
}

function drum(step: number, voice: DrumKind, velocity: number): SongEvent {
  return { step, voice, midi: 0, length: 1, velocity };
}
